import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { api, mediaUrl, formatApiErrorDetail } from "../../lib/api";
import { useContent } from "../../context/ContentContext";

const FT = {
  pt: { name: "O teu nome", email: "O teu email", subject: "Assunto (opcional)", message: "A tua mensagem", send: "Enviar mensagem", sending: "A enviar...", required: "Preenche o nome, email e mensagem." },
  en: { name: "Your name", email: "Your email", subject: "Subject (optional)", message: "Your message", send: "Send message", sending: "Sending...", required: "Please fill in name, email and message." },
};

function Heading({ eyebrow, title, intro }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
      <div>
        {eyebrow ? <span className="label-mono text-peca-yellow">✳ {eyebrow}</span> : null}
        {title ? (
          <h2 className="mt-5 font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tight leading-[0.95] text-peca-cream max-w-2xl">
            {title}
          </h2>
        ) : null}
      </div>
      {intro ? <p className="text-peca-grey max-w-sm text-base leading-relaxed">{intro}</p> : null}
    </div>
  );
}

function rich(text) {
  return String(text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**")
      ? <strong key={i} className="text-peca-cream font-bold">{p.slice(2, -2)}</strong>
      : p
  );
}

function TextBlock({ section }) {
  const paras = (section.body || "").split("\n").filter((p) => p.trim() !== "");
  const hasIntro = Boolean(section.intro);
  return (
    <div className="grid md:grid-cols-12 gap-10">
      <div className="md:col-span-4">
        {section.eyebrow ? <span className="label-mono text-peca-yellow">✳ {section.eyebrow}</span> : null}
        <h2 className="mt-5 font-display font-extrabold uppercase text-3xl md:text-4xl tracking-tight leading-[0.95] text-peca-cream break-words hyphens-auto">
          {section.title}
        </h2>
      </div>
      <div className={`flex flex-col gap-5 ${hasIntro ? "md:col-span-7 md:col-start-6" : "md:col-span-8 md:col-start-5"}`}>
        {hasIntro ? <p className="text-peca-cream text-lg md:text-2xl font-display leading-snug">{rich(section.intro)}</p> : null}
        {paras.map((p, i) => (
          <p key={i} className="text-peca-grey text-base md:text-lg leading-relaxed">{rich(p)}</p>
        ))}
      </div>
    </div>
  );
}

function CardsBlock({ section }) {
  const cards = section.cards || [];
  return (
    <>
      <Heading eyebrow={section.eyebrow} title={section.title} intro={section.intro} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((card, i) => {
          const Wrapper = card.link ? "a" : "div";
          const props = card.link ? { href: card.link, target: "_blank", rel: "noopener noreferrer" } : {};
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}>
              <Wrapper {...props} className="group relative overflow-hidden border border-peca-line bg-peca-surface min-h-[280px] flex flex-col justify-between p-6 hover:border-peca-yellow transition-colors duration-300">
                {card.image ? (
                  <div className="absolute inset-0 opacity-20 group-hover:opacity-40 group-hover:scale-105 transition-all duration-500 grayscale" style={{ backgroundImage: `url(${mediaUrl(card.image)})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-t from-peca-surface via-peca-surface/80 to-transparent" />
                <div className="relative flex items-start justify-between">
                  <span className="font-mono-p text-peca-yellow text-sm">{String(i + 1).padStart(2, "0")}</span>
                  {card.link ? <ArrowUpRight size={22} className="text-peca-grey group-hover:text-peca-yellow group-hover:rotate-45 transition-all duration-300" /> : null}
                </div>
                <div className="relative">
                  <h3 className="font-display font-bold uppercase text-2xl md:text-3xl tracking-tight text-peca-cream mb-3">{card.title}</h3>
                  <p className="text-peca-grey text-sm leading-relaxed max-w-sm">{rich(card.description)}</p>
                </div>
              </Wrapper>
            </motion.div>
          );
        })}
      </div>
    </>
  );
}

function PortfolioBlock({ section }) {
  const projects = section.projects || [];
  return (
    <>
      <Heading eyebrow={section.eyebrow} title={section.title} intro={section.intro} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {projects.map((project, i) => {
          const Wrapper = project.link ? "a" : "div";
          const props = project.link ? { href: project.link, target: "_blank", rel: "noopener noreferrer" } : {};
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: (i % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}>
              <Wrapper {...props} className="group block">
                <div className="relative overflow-hidden aspect-[4/3] bg-peca-surface">
                  {project.image ? <img src={mediaUrl(project.image)} alt={project.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" /> : null}
                </div>
                <div className="pt-5">
                  {project.category ? <span className="label-mono text-peca-yellow">{project.category}</span> : null}
                  <h3 className="mt-2 font-display font-bold uppercase text-2xl tracking-tight text-peca-cream group-hover:translate-x-1 transition-transform duration-300">{project.title}</h3>
                  {project.client ? <p className="text-peca-grey text-sm mt-1">{project.client}</p> : null}
                </div>
              </Wrapper>
            </motion.div>
          );
        })}
      </div>
    </>
  );
}

function FormBlock({ section }) {
  const { locale } = useContent();
  const t = FT[locale] || FT.pt;
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full bg-transparent border-b-2 border-peca-line focus:border-peca-yellow outline-none py-4 text-peca-cream placeholder:text-peca-grey/60 font-body transition-colors duration-200";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.required || "Preenche o nome, email e mensagem.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", { ...form, subject: form.subject || section.title || "Formulário" });
      toast.success(section.successMessage || "Mensagem enviada!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Falha ao enviar.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        {section.eyebrow ? <span className="label-mono text-peca-yellow">✳ {section.eyebrow}</span> : null}
        <h2 className="mt-5 font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tight leading-[0.9] text-peca-cream">{section.title}</h2>
        {section.intro ? <p className="mt-6 text-peca-grey text-base md:text-lg leading-relaxed max-w-md">{section.intro}</p> : null}
      </div>
      <form onSubmit={submit} data-testid={`form-block-${section.id}`} className="md:col-span-6 md:col-start-7 flex flex-col gap-6">
        <input data-testid={`form-${section.id}-name`} className={inputCls} placeholder={t.name} value={form.name} onChange={update("name")} />
        <input data-testid={`form-${section.id}-email`} type="email" className={inputCls} placeholder={t.email} value={form.email} onChange={update("email")} />
        <input className={inputCls} placeholder={t.subject} value={form.subject} onChange={update("subject")} />
        <textarea data-testid={`form-${section.id}-message`} rows={4} className={`${inputCls} resize-none`} placeholder={t.message} value={form.message} onChange={update("message")} />
        <button type="submit" disabled={sending} data-testid={`form-${section.id}-submit`} className="group mt-2 inline-flex items-center justify-center gap-2 bg-peca-yellow text-peca-black font-display font-bold uppercase text-sm tracking-wide px-8 py-5 hover:bg-peca-cream transition-colors duration-200 disabled:opacity-60">
          {sending ? <Loader2 size={18} className="animate-spin" /> : null}
          {sending ? t.sending : t.send}
          {!sending && <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform duration-200" />}
        </button>
      </form>
    </div>
  );
}

export default function TypedSection({ section }) {
  if (!section || section.visible === false) return null;
  return (
    <section id={section.id} data-testid={`typed-section-${section.id}`} data-type={section.type} className="py-24 md:py-40 border-t border-peca-line">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        {section.type === "text" && <TextBlock section={section} />}
        {section.type === "cards" && <CardsBlock section={section} />}
        {section.type === "portfolio" && <PortfolioBlock section={section} />}
        {section.type === "form" && <FormBlock section={section} />}
      </div>
    </section>
  );
}
